import { createContext, type Dispatch, type ReactNode, useContext, useReducer } from 'react'
import { isNil } from 'utils'

/**
 * Represents the state used for managing all modals' visibility and related behaviors in Planning -> Fields
 *
 * This type is used to track the open or closed states of specific modals
 * and any relevant options or handlers associated with them.
 */
type ModalStateType = {
  /**
   * Whether the "Add CFF" modal is open
   */
  addCffOpen: boolean
  /**
   * Whether the "Invalid Plan" modal is open
   */
  invalidPlanOpen: boolean
  /**
   * (Optional) Function to handle the non-close/cancel action associated with a modal
   */
  actionHandler?: () => void
}

const defaultModalState: ModalStateType = {
  addCffOpen: false,
  invalidPlanOpen: false,
  actionHandler: undefined
}

export enum ModalActionOption {
  Close = 'close',
  AddCFFOpen = 'addCffOpen',
  InvalidPlanOpen = 'invalidPlanOpen',
  ActionHandler = 'actionHandler'
}

type ModalAction =
  | { type: ModalActionOption.Close }
  | {
      type: ModalActionOption.AddCFFOpen | ModalActionOption.InvalidPlanOpen
      value: boolean
    }
  | {
      type: ModalActionOption.ActionHandler
      value: () => undefined | void
    }

const PlanningModalsContext = createContext<ModalStateType | null>(null)
const PlanningModalsDispatchContext = createContext<Dispatch<ModalAction> | null>(null)

export const usePlanningModalsContext = () => {
  const planningModalsContext = useContext(PlanningModalsContext)
  if (isNil(planningModalsContext))
    throw new Error(
      'No PlanningModalsContext.Provider found when calling usePlanningModalsContext.'
    )
  return planningModalsContext
}

export const usePlanningModalsDispatchContext = () => {
  const planningModalsDispatchContext = useContext(PlanningModalsDispatchContext)
  if (!planningModalsDispatchContext)
    throw new Error(
      'No PlanningModalsDispatchContext.Provider found when calling usePlanningModalsDispatchContext.'
    )
  return planningModalsDispatchContext
}

const modalReducer = (modalState: ModalStateType, action: ModalAction) => {
  switch (action.type) {
    case ModalActionOption.Close:
      return defaultModalState
    case ModalActionOption.AddCFFOpen:
    case ModalActionOption.InvalidPlanOpen:
    case ModalActionOption.ActionHandler:
      return {
        ...modalState,
        [action.type]: action.value
      }
  }
}

export const PlanningModalsProvider = ({ children }: { children: ReactNode }) => {
  const [modalState, dispatch] = useReducer(modalReducer, defaultModalState)

  return (
    <PlanningModalsContext value={modalState}>
      <PlanningModalsDispatchContext value={dispatch}>{children}</PlanningModalsDispatchContext>
    </PlanningModalsContext>
  )
}
